// Volume Indicators
export class VolumeIndicators {
    /**
     * Calculate On Balance Volume (OBV)
     */
    static calculateOBV(prices: number[], volumes: number[]): number[] {
        const obv: number[] = [volumes[0]];
        for (let i = 1; i < prices.length; i++) {
            if (prices[i] > prices[i - 1]) {
                obv.push(obv[i - 1] + volumes[i]);
            } else if (prices[i] < prices[i - 1]) {
                obv.push(obv[i - 1] - volumes[i]);
            } else {
                obv.push(obv[i - 1]);
            }
        }
        return obv;
    }

    /**
     * Calculate Chaikin Money Flow (CMF)
     */
    static calculateCMF(prices: number[], highs: number[], lows: number[], volumes: number[]): number[] {
        const cmf: number[] = [];
        const period = 20;

        if (prices.length < period) return [];

        for (let i = period - 1; i < prices.length; i++) {
            let totalMoneyFlowVolume = 0;
            let totalVolume = 0;

            for (let j = i - period + 1; j <= i; j++) {
                const high = highs[j] || prices[j];
                const low = lows[j] || prices[j];
                const close = prices[j];
                const volume = volumes[j] || 1;

                const moneyFlowMultiplier = (close - low - (high - close)) / (high - low);
                const moneyFlowVolume = moneyFlowMultiplier * volume;

                totalMoneyFlowVolume += moneyFlowVolume;
                totalVolume += volume;
            }

            const cmfValue = totalVolume !== 0 ? totalMoneyFlowVolume / totalVolume : 0;
            cmf.push(cmfValue);
        }

        return cmf;
    }

    /**
     * Calculate Accumulation/Distribution Line (ADL)
     */
    static calculateADL(prices: number[], highs: number[], lows: number[], volumes: number[]): number[] {
        const adl: number[] = [0];

        for (let i = 1; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            const close = prices[i];
            const volume = volumes[i] || 1;

            // Money Flow Multiplier
            const moneyFlowMultiplier = (close - low - (high - close)) / (high - low);

            // Money Flow Volume
            const moneyFlowVolume = moneyFlowMultiplier * volume;

            // Accumulation/Distribution Line
            adl.push(adl[i - 1] + moneyFlowVolume);
        }

        return adl;
    }

    /**
     * Calculate Volume Rate of Change
     */
    static calculateVolumeROC(volumes: number[]): number[] {
        const volumeROC: number[] = [];
        for (let i = 10; i < volumes.length; i++) {
            volumeROC.push(((volumes[i] - volumes[i - 10]) / volumes[i - 10]) * 100);
        }
        return volumeROC;
    }

    /**
     * Calculate Money Flow Index (MFI)
     */
    static calculateMFI(prices: number[], highs: number[], lows: number[], volumes: number[]): number[] {
        const mfi: number[] = [];
        const period = 14;

        if (prices.length < period + 1) return [];

        for (let i = period; i < prices.length; i++) {
            let positiveMoneyFlow = 0;
            let negativeMoneyFlow = 0;

            for (let j = i - period + 1; j <= i; j++) {
                const high = highs[j] || prices[j];
                const low = lows[j] || prices[j];
                const close = prices[j];
                const volume = volumes[j] || 1;

                const typicalPrice = (high + low + close) / 3;
                const moneyFlow = typicalPrice * volume;

                if (j > i - period + 1) {
                    const prevHigh = highs[j - 1] || prices[j - 1];
                    const prevLow = lows[j - 1] || prices[j - 1];
                    const prevClose = prices[j - 1];
                    const prevTypicalPrice = (prevHigh + prevLow + prevClose) / 3;

                    if (typicalPrice > prevTypicalPrice) {
                        positiveMoneyFlow += moneyFlow;
                    } else if (typicalPrice < prevTypicalPrice) {
                        negativeMoneyFlow += moneyFlow;
                    }
                }
            }

            const moneyRatio = negativeMoneyFlow !== 0 ? positiveMoneyFlow / negativeMoneyFlow : 0;
            const mfiValue = 100 - 100 / (1 + moneyRatio);
            mfi.push(mfiValue);
        }

        return mfi;
    }

    /**
     * Calculate Volume Weighted Average Price (VWAP)
     */
    static calculateVWAP(prices: number[], volumes: number[]): number[] {
        const vwap: number[] = [];
        let cumulativePV = 0;
        let cumulativeVolume = 0;

        for (let i = 0; i < prices.length; i++) {
            cumulativePV += prices[i] * (volumes[i] || 1);
            cumulativeVolume += volumes[i] || 1;
            vwap.push(cumulativePV / cumulativeVolume);
        }

        return vwap;
    }
}
